// FloatingChat.js
import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  Badge,
  Tabs,
  Tab,
  Button,
  Chip,
  useTheme,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Close,
  Send,
  Chat as ChatIcon,
  Groups,
  PersonAdd,
  Group,
  ArrowBack,
  Circle,
  CheckCircle,
} from "@mui/icons-material";
import { useChat } from "../../contexts/ChatContext";
import { useAuth } from "../../hooks/useAuth";

const FloatingChat = () => {
  const theme = useTheme();
  const {
    isOpen,
    activeRoom,
    showNewChat,
    myRooms,
    roomMessages,
    availableUsers,
    totalUnread,
    closeChat,
    setActiveRoom,
    setShowNewChat,
    startDirectChat,
    createGroup,
    sendMessage,
    getRoomName,
    getUnreadCount,
  } = useChat();

  const { user } = useAuth();
  const [messageText, setMessageText] = useState("");
  const [newGroupName, setNewGroupName] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [roomMessages]);

  const handleSend = () => {
    if (messageText.trim()) {
      sendMessage(messageText);
      setMessageText("");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const toggleMember = (userId) => {
    setSelectedMembers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  };

  const handleCreateGroup = () => {
    if (newGroupName.trim() && selectedMembers.length > 0) {
      createGroup(newGroupName.trim(), selectedMembers);
      setNewGroupName("");
      setSelectedMembers([]);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;

    if (diff < 86400000 && date.getDate() === now.getDate()) {
      return date.toLocaleTimeString("es-ES", {
        hour: "2-digit",
        minute: "2-digit",
      });
    }
    if (diff < 172800000) return "Ayer";
    return date.toLocaleDateString("es-ES", { day: "numeric", month: "short" });
  };

  if (!isOpen) return null;

  return (
    <Paper
      elevation={8}
      sx={{
        position: "fixed",
        bottom: { xs: 0, sm: 16 },
        right: { xs: 0, sm: 16 },
        left: { xs: 0, sm: "auto" },
        width: { xs: "100%", sm: 380 },
        height: { xs: "100%", sm: 550 },
        maxHeight: { xs: "100vh", sm: 550 },
        borderRadius: { xs: 0, sm: "20px" },
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        zIndex: 9999,
        border: "1px solid",
        borderColor: { xs: "transparent", sm: theme.palette.divider },
        backgroundColor: theme.palette.background.paper,
      }}
    >
      {/* Header */}
      {activeRoom ? (
        <Box
          sx={{
            p: { xs: 1, sm: 1.5 },
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            gap: 1,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: "white",
            flexShrink: 0,
          }}
        >
          <IconButton
            size="small"
            onClick={() => setActiveRoom(null)}
            sx={{ color: "white" }}
          >
            <ArrowBack />
          </IconButton>
          <Avatar
            sx={{
              width: { xs: 28, sm: 32 },
              height: { xs: 28, sm: 32 },
              bgcolor: "rgba(255,255,255,0.3)",
              fontSize: { xs: "0.7rem", sm: "0.8rem" },
            }}
          >
            {getRoomName(activeRoom)[0]?.toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
              {getRoomName(activeRoom)}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8, fontSize: { xs: "0.65rem", sm: "0.75rem" } }}>
              {activeRoom.type === "group"
                ? `${activeRoom.members?.length} miembros`
                : "Chat directo"}
            </Typography>
          </Box>
          <IconButton size="small" onClick={closeChat} sx={{ color: "white" }}>
            <Close sx={{ fontSize: { xs: 20, sm: 24 } }} />
          </IconButton>
        </Box>
      ) : (
        <Box
          sx={{
            p: { xs: 1, sm: 1.5 },
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            gap: 1,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: "white",
            flexShrink: 0,
          }}
        >
          <ChatIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
          <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
            Chats
          </Typography>
          <IconButton
            size="small"
            onClick={() => setShowNewChat(!showNewChat)}
            sx={{ color: "white" }}
          >
            {showNewChat ? <ArrowBack sx={{ fontSize: { xs: 20, sm: 24 } }} /> : <PersonAdd sx={{ fontSize: { xs: 20, sm: 24 } }} />}
          </IconButton>
          <IconButton size="small" onClick={closeChat} sx={{ color: "white" }}>
            <Close sx={{ fontSize: { xs: 20, sm: 24 } }} />
          </IconButton>
        </Box>
      )}

      {/* Contenido */}
      {activeRoom ? (
        <>
          {/* Mensajes */}
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              p: { xs: 1.5, sm: 2 },
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.02)"
                  : "#f8fafc",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {roomMessages.map((msg, i) => {
              const isMine = msg.senderId === user.uid;
              return (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    justifyContent: isMine ? "flex-end" : "flex-start",
                    mb: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: { xs: "85%", sm: "75%" },
                      p: { xs: 1, sm: 1.2 },
                      borderRadius: isMine
                        ? "16px 16px 4px 16px"
                        : "16px 16px 16px 4px",
                      backgroundColor: isMine
                        ? theme.palette.primary.main
                        : theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.1)"
                          : "#e2e8f0",
                      color: isMine ? "white" : "text.primary",
                    }}
                  >
                    {!isMine && (
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        sx={{
                          display: "block",
                          mb: 0.3,
                          color: theme.palette.primary.main,
                          fontSize: { xs: "0.7rem", sm: "0.75rem" },
                        }}
                      >
                        {msg.senderName}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, wordBreak: "break-word" }}>
                      {msg.text}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        textAlign: "right",
                        mt: 0.3,
                        opacity: 0.7,
                        fontSize: { xs: "0.6rem", sm: "0.65rem" },
                      }}
                    >
                      {formatTime(msg.timestamp)}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
            <div ref={messagesEndRef} />
          </Box>

          {/* Input */}
          <Box
            sx={{
              p: { xs: 1, sm: 1.5 },
              borderTop: 1,
              borderColor: "divider",
              display: "flex",
              gap: 1,
              flexShrink: 0,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder="Escribe un mensaje..."
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              onKeyPress={handleKeyPress}
              multiline
              maxRows={3}
              inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "20px",
                  backgroundColor: theme.palette.background.paper,
                },
              }}
            />
            <IconButton
              onClick={handleSend}
              sx={{
                alignSelf: "flex-end",
                backgroundColor: theme.palette.primary.main,
                color: "white",
                width: { xs: 36, sm: 40 },
                height: { xs: 36, sm: 40 },
                "&:hover": { backgroundColor: theme.palette.primary.dark },
              }}
            >
              <Send sx={{ fontSize: { xs: 18, sm: 20 } }} />
            </IconButton>
          </Box>
        </>
      ) : showNewChat ? (
        <Box sx={{ flex: 1, overflow: "auto", p: { xs: 1.5, sm: 2 }, WebkitOverflowScrolling: "touch" }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ fontSize: { xs: "0.85rem", sm: "0.9rem" } }}>
            Nuevo grupo
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Nombre del grupo"
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
          />

          <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>
            Selecciona miembros ({selectedMembers.length})
          </Typography>
          <List dense sx={{ mt: 1 }}>
            {availableUsers.map((u) => (
              <ListItem
                key={u.id}
                button
                onClick={() => toggleMember(u.id)}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  backgroundColor: selectedMembers.includes(u.id)
                    ? "rgba(0, 175, 163, 0.1)"
                    : "transparent",
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    sx={{
                      width: { xs: 28, sm: 32 },
                      height: { xs: 28, sm: 32 },
                      bgcolor: theme.palette.secondary.main,
                      fontSize: { xs: "0.7rem", sm: "0.8rem" },
                    }}
                  >
                    {(u.displayName || u.email)?.charAt(0)?.toUpperCase()}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={u.displayName || u.email}
                  secondary={u.email}
                  primaryTypographyProps={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
                  secondaryTypographyProps={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                />
                {selectedMembers.includes(u.id) && (
                  <CheckCircle color="primary" sx={{ fontSize: { xs: 18, sm: 20 } }} />
                )}
              </ListItem>
            ))}
          </List>

          <Button
            variant="contained"
            fullWidth
            onClick={handleCreateGroup}
            disabled={!newGroupName.trim() || selectedMembers.length === 0}
            startIcon={<Group />}
            sx={{ borderRadius: "12px", mt: 1, textTransform: "none", fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
          >
            Crear grupo
          </Button>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ fontSize: { xs: "0.85rem", sm: "0.9rem" } }}>
            Chat directo
          </Typography>
          <List dense>
            {availableUsers.slice(0, 5).map((u) => (
              <ListItem
                key={u.id}
                button
                onClick={() => startDirectChat(u.id)}
                sx={{ borderRadius: 2, mb: 0.5 }}
              >
                <ListItemAvatar>
                  <Avatar
                    sx={{
                      width: { xs: 28, sm: 32 },
                      height: { xs: 28, sm: 32 },
                      bgcolor: theme.palette.primary.main,
                      fontSize: { xs: "0.7rem", sm: "0.8rem" },
                    }}
                  >
                    {(u.displayName || u.email)?.charAt(0)?.toUpperCase()}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={u.displayName || u.email}
                  secondary="Iniciar chat"
                  primaryTypographyProps={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
                  secondaryTypographyProps={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      ) : (
        <Box sx={{ flex: 1, overflow: "auto", WebkitOverflowScrolling: "touch" }}>
          {myRooms.length === 0 ? (
            <Box sx={{ p: { xs: 3, sm: 4 }, textAlign: "center" }}>
              <ChatIcon sx={{ fontSize: { xs: 36, sm: 48 }, color: "text.disabled", mb: 1 }} />
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                No tienes conversaciones
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowNewChat(true)}
                sx={{ mt: 1.5, borderRadius: "12px", textTransform: "none", fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
              >
                Iniciar chat
              </Button>
            </Box>
          ) : (
            <List dense>
              {myRooms.map((room) => {
                const unread = getUnreadCount(room.id);
                return (
                  <ListItem
                    key={room.id}
                    button
                    onClick={() => setActiveRoom(room)}
                    sx={{
                      borderBottom: 1,
                      borderColor: "divider",
                      "&:hover": { backgroundColor: "action.hover" },
                    }}
                  >
                    <ListItemAvatar>
                      <Badge
                        color="success"
                        variant="dot"
                        invisible={!room.members?.length}
                      >
                        <Avatar
                          sx={{
                            width: { xs: 36, sm: 40 },
                            height: { xs: 36, sm: 40 },
                            bgcolor:
                              room.type === "group"
                                ? theme.palette.secondary.main
                                : theme.palette.primary.main,
                          }}
                        >
                          {room.type === "group" ? (
                            <Groups sx={{ fontSize: { xs: 18, sm: 20 } }} />
                          ) : (
                            <Typography sx={{ fontSize: { xs: "0.8rem", sm: "0.9rem" } }}>
                              {getRoomName(room)[0]?.toUpperCase()}
                            </Typography>
                          )}
                        </Avatar>
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      primary={getRoomName(room)}
                      secondary={room.lastMessage || "Sin mensajes"}
                      primaryTypographyProps={{
                        fontWeight: unread > 0 ? 600 : 400,
                        fontSize: { xs: "0.8rem", sm: "0.85rem" },
                      }}
                      secondaryTypographyProps={{
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                        noWrap: true,
                      }}
                    />
                    <Box sx={{ textAlign: "right", flexShrink: 0, ml: 1 }}>
                      {room.lastMessageTime && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: { xs: "0.6rem", sm: "0.65rem" } }}
                        >
                          {formatTime(room.lastMessageTime)}
                        </Typography>
                      )}
                      {unread > 0 && (
                        <Chip
                          label={unread}
                          size="small"
                          color="primary"
                          sx={{ height: { xs: 18, sm: 20 }, fontSize: { xs: "0.6rem", sm: "0.65rem" }, mt: 0.3 }}
                        />
                      )}
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default FloatingChat;